import { Link } from "react-router";
import type { OnboardingChecklist as Checklist, OnboardingStep } from "@/types/models";

// New-owner setup guide (Phase 7). Renders only while the farm's essentials are
// incomplete — once the required steps are done it disappears and the normal
// dashboard is unobstructed. Nothing is stored; completion is derived from data.
//
// It is laid out as ONE BAND, not a panel. As a vertical list with a two-line
// header and a second stacked section for the optional steps it stood ~290px
// tall, so the first thing an owner saw on their own farm's dashboard was a
// setup list and the farm's numbers began below the fold. The steps are all
// still here and still linked — a checklist that hides its steps is a progress
// bar — they are just laid along the band instead of down it.
export default function OnboardingChecklist({ checklist }: { checklist: Checklist | null }) {
  if (!checklist || checklist.complete) return null;

  const essential = checklist.steps.filter((s) => !s.optional);
  const recommended = checklist.steps.filter((s) => s.optional);
  const doneCount = essential.filter((s) => s.done).length;
  const pct = essential.length > 0 ? (doneCount / essential.length) * 100 : 0;

  return (
    <div className="mb-6 rounded-2xl border border-brand-200 bg-brand-50/40 px-5 py-4 dark:border-brand-500/30 dark:bg-brand-500/[0.06]">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
          Finish setting up your farm
        </h3>
        <div className="flex items-center gap-2.5">
          <div
            className="h-1.5 w-24 overflow-hidden rounded-full bg-brand-100 dark:bg-white/10"
            aria-hidden
          >
            <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="shrink-0 text-xs font-medium text-gray-500 num dark:text-gray-400">
            {doneCount} of {essential.length} done
          </span>
        </div>
      </div>

      <ul className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        {essential.map((s) => (
          <StepChip key={s.key} step={s} />
        ))}
      </ul>

      {recommended.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-brand-200/60 pt-2.5 dark:border-white/10">
          <span className="stat-label">Recommended</span>
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {recommended.map((s) => (
              <StepChip key={s.key} step={s} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StepChip({ step }: { step: OnboardingStep }) {
  const mark = step.done ? (
    <span
      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success-500 text-white"
      aria-hidden
    >
      <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none">
        <path
          d="M2.5 6.5L5 9L9.5 3.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  ) : (
    <span
      className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300 dark:border-gray-600"
      aria-hidden
    />
  );

  const label = step.done ? (
    <span className="text-sm text-gray-400 line-through dark:text-gray-500">{step.label}</span>
  ) : (
    <Link
      to={step.link}
      className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
    >
      {step.label}
    </Link>
  );

  return (
    <li className="flex items-center gap-2">
      {mark}
      {label}
    </li>
  );
}
